import { Loader } from "components/loader/Loader";
import React, { useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import * as d3 from "d3";
import { Header } from "./Header";
import { getAllEquipment } from "redux/actions/equipmentActions";
import { handleErrors } from "utils";
import { getFloor, getSvg } from "redux/actions/floorActions";
import { RoomEquipmentResponse, WorkstationEquipmentResponse } from "api/generated";
import { useSearchParams } from "react-router-dom";
import { Autocomplete, Box, styled, TextField } from "@mui/material";

const AutoComplete = styled(Autocomplete)`
  & .MuiInputBase-input {
    height: 1rem;
    margin: 5;
  }
`;

export const LocationsByInventoryNumbers = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { allEquipment, allEquipmentLoadingState } = useAppSelector((state) => state.equipment);
  const { floor } = useAppSelector((state) => state.floor);

  useEffect(() => {
    dispatch(getAllEquipment());
  }, [dispatch]);

  const inventoryNumber = searchParams.get("inventoryNumber");

  const selectedEquipment = useMemo(
    () => allEquipment.find((eq) => eq.inventoryNumber?.toUpperCase() === inventoryNumber?.toUpperCase()),
    [allEquipment, inventoryNumber],
  );

  useEffect(() => {
    if (!selectedEquipment) {
      // RESET SVG IF NO EQUIPMENT IS SELECTED
      d3.select(`#svg_container`).html("");
      return;
    }

    if (selectedEquipment.equipmentType === "WORKSTATION") {
      showWorkstationLocation(selectedEquipment, dispatch);
    }
    switch (selectedEquipment.equipmentType) {
      case "WORKSTATION":
        showWorkstationLocation(selectedEquipment, dispatch);
        break;
      case "ROOM":
        showRoomLocation(selectedEquipment, dispatch);
    }
  }, [dispatch, selectedEquipment]);

  if (allEquipmentLoadingState === "NotLoaded" || allEquipmentLoadingState === "Loading") {
    return <Loader />;
  }

  return (
    <main className="main">
      <Header />
      <div className="stats">
        <div className="wrap stats__wrap">
          <div className="floor">
            <h1 className="floor__title">
              <AutoComplete
                onChange={(_, value: unknown) => {
                  setSearchParams(value ? { inventoryNumber: value as string } : {});
                }}
                value={inventoryNumber}
                options={allEquipment.map((eq) => eq.inventoryNumber!)}
                getOptionLabel={(option) => ` ${option}`}
                renderOption={(props, option) => {
                  const equipment = option as string;
                  return (
                    <Box component="li" {...props} key={equipment}>
                      <div>{equipment}</div>
                    </Box>
                  );
                }}
                sx={{ width: 260, background: "white" }}
                renderInput={(params) => <TextField {...params} label="Find equipment" placeholder={"Search..."} />}
              />
              {selectedEquipment && (
                <>
                  {" "}
                  <span className="floor__title-left">{floor?.nameForUsers}</span>
                  <a
                    className="floor__link"
                    href={`https://www.google.com/maps?q=${floor?.building?.latitude},${floor?.building?.longitude}`}
                    target="_blank"
                    rel="noreferrer">
                    <span className="floor__title-right">
                      {floor?.building?.address}, {floor?.building?.city?.name}, {floor?.building?.city?.country?.name}
                    </span>
                  </a>
                </>
              )}
            </h1>
            <div className="floor__plan" id="svg_container"></div>
          </div>
        </div>
      </div>
    </main>
  );
};

const showRoomLocation = (equipment: RoomEquipmentResponse, dispatch: ReturnType<typeof useAppDispatch>) => {
  const floorId = equipment.room!.floor!.id;
  const roomId = equipment.room!.id;

  dispatch(getFloor(Number(floorId)))
    .unwrap()
    .then((res) => {
      const mapId = res.floorMapId;

      mapId &&
        dispatch(getSvg(mapId))
          .unwrap()
          .then((res) => {
            var reader = new FileReader();
            reader.readAsDataURL(new File([res], ""));
            reader.onload = function (event) {
              // @ts-ignore
              const fileData: string = event?.target?.result;

              d3.xml(fileData).then((data) => {
                // @ts-ignore
                d3.select(`#svg_container`).html("");
                // @ts-ignore
                d3.select(`#svg_container`)
                  .node()
                  // @ts-ignore
                  ?.append(data.documentElement);

                const sizes = d3.select("#Layer_1").attr("viewBox").split(" ");

                const width = sizes[2],
                  height = sizes[3];
                const zoom = d3
                  .zoom()
                  .scaleExtent([0.5, 3])
                  .translateExtent([
                    [0, 0],
                    [Number(width), Number(height)],
                  ])
                  .on("zoom", (e) => {
                    d3.select("svg g").attr("transform", e.transform);
                  });
                // @ts-ignore
                d3.select("#Layer_1").call(zoom).on("dblclick.zoom", null);

                if (roomId !== undefined) {
                  const room = d3.select(`[id="${roomId}"]`);
                  if (!room.empty()) {
                    room.style("fill", "var(--orange)");
                  }

                  const roomName = d3.select(`[id*="_name_${roomId}"]`);
                  if (!roomName.empty()) {
                    roomName.style("fill", "black");
                  }
                }
              });
            };
          })
          .catch((res) => {
            handleErrors(res);
          });
    });
};

const showWorkstationLocation = (
  equipment: WorkstationEquipmentResponse,
  dispatch: ReturnType<typeof useAppDispatch>,
) => {
  const floorId = equipment.workstation!.room!.floor!.id;
  const workstationId = equipment.workstation!.id;
  const officeId = equipment.workstation!.room!.id;

  dispatch(getFloor(Number(floorId)))
    .unwrap()
    .then((res) => {
      const mapId = res.floorMapId;

      mapId &&
        dispatch(getSvg(mapId))
          .unwrap()
          .then((res) => {
            var reader = new FileReader();
            reader.readAsDataURL(new File([res], ""));
            reader.onload = function (event) {
              // @ts-ignore
              const fileData: string = event?.target?.result;

              d3.xml(fileData).then((data) => {
                // @ts-ignore
                d3.select(`#svg_container`).html("");
                // @ts-ignore
                d3.select(`#svg_container`)
                  .node()
                  // @ts-ignore
                  ?.append(data.documentElement);

                const sizes = d3.select("#Layer_1").attr("viewBox").split(" ");

                const width = sizes[2],
                  height = sizes[3];
                const zoom = d3
                  .zoom()
                  .scaleExtent([0.5, 3])
                  .translateExtent([
                    [0, 0],
                    [Number(width), Number(height)],
                  ])
                  .on("zoom", (e) => {
                    d3.select("svg g").attr("transform", e.transform);
                  });
                // @ts-ignore
                d3.select("#Layer_1").call(zoom).on("dblclick.zoom", null);

                if (workstationId !== undefined && officeId !== undefined) {
                  const table = d3.select(`#path_${workstationId}_office_${officeId}`);
                  const user = d3.select(`#user_${workstationId}_office_${officeId}`);
                  const company = d3.select(`#company_${workstationId}_office_${officeId}`);

                  if (!table.empty()) {
                    table.style("fill", "var(--orange)");
                  }
                  if (!user.empty()) {
                    user.style("fill", "black");
                  }
                  if (!company.empty()) {
                    company.style("fill", "black");
                  }
                }
              });
            };
          })
          .catch((res) => {
            handleErrors(res);
          });
    });
};
